import React, {useCallback, useContext, useEffect} from 'react';
import {useSelector} from 'react-redux';
import {useNavigate} from 'react-router-dom';

import {selectCriteriaStripView} from 'src/components/criteria-strip/services/criteria-strip-slice';
import {MessageContext} from 'src/contexts/message-context';
import {useEventTracking} from 'src/hooks/useEventTracking';

import Button from '@pb/kit-component-button';
import {Tooltip} from '@pb/kit-component-popups';

type Props = {
    activeTab: string,
    searchId: string,
};

const SHORTCUT_HINT = 'Press Ctrl+Enter or Command+Enter to search';
const EMPTY_CRITERIA_MESSAGE = 'Please enter the search criteria';

export const SearchButton = ({activeTab, searchId}: Props) => {
    const {searchCriteriaRun} = useEventTracking();
    const navigate = useNavigate();
    const criteria = useSelector(selectCriteriaStripView);
    const {setInfoMessage} = useContext(MessageContext);

    const isCriteriaEmpty = (searchCriteria: object[] | null) => {
        return !searchCriteria || Object.keys(searchCriteria).length === 0;
    };

    const handleClick = useCallback(() => {
        if (isCriteriaEmpty(criteria)) {
            setInfoMessage(EMPTY_CRITERIA_MESSAGE);
            return;
        }
        searchCriteriaRun();
        navigate(`/search-result/${searchId}/${activeTab}`);
    }, [searchId, activeTab, criteria, navigate, searchCriteriaRun, setInfoMessage]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
                handleClick();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [handleClick]);

    return (
        <Tooltip anchor={({anchorRef, getAnchorProps}) => (
            <Button
                ref={anchorRef}
                {...getAnchorProps()}
                variant="Type.PRIMARY"
                disabled={isCriteriaEmpty(criteria)}
                onClick={handleClick}>
                Search
            </Button>
        )}>
            {isCriteriaEmpty(criteria) ? EMPTY_CRITERIA_MESSAGE : SHORTCUT_HINT}
        </Tooltip>
    );
};
